import { useState, forwardRef } from 'react';
import { Button } from '@/components/ui/button';
import { RichTextRenderer } from '../common/RichTextRenderer';
import { useTranslate } from '@/i18n';

interface ProducerAboutProps {
  description: string;
  shortDescription?: string | null;
  title?: string;
}

export const ProducerAbout = forwardRef<HTMLElement, ProducerAboutProps>(
  function ProducerAbout({ description, shortDescription, title }, ref) {
    const t = useTranslate();
    const heading = title ?? t("producer.about.heading");
    const [expanded, setExpanded] = useState(false);

    if (!description) return null;

    const isHtml = /<[a-z][\s\S]*>/i.test(description);

    const plainText = isHtml 
      ? (() => {
          const temp = document.createElement('div');
          temp.innerHTML = description;
          return temp.textContent || temp.innerText || '';
        })()
      : description;

    const truncateThreshold = 5000;
    const shouldTruncate = plainText.length > truncateThreshold;

    const truncatedPlainText = shouldTruncate && !expanded
      ? plainText.slice(0, truncateThreshold).trim() + '...'
      : plainText;

    return (
      <section ref={ref}>
        <div className="max-w-2xl">
          <h2 className="font-display text-lg text-foreground mb-3">
            {heading}
          </h2>

          {shortDescription?.trim() && (
            <p className="text-foreground/80 font-medium leading-relaxed mb-4">
              {shortDescription}
            </p>
          )}

          {isHtml ? (
            <div className="relative">
              <div 
                className={`farmly-richtext transition-all ${
                  !expanded && shouldTruncate ? 'max-h-24 overflow-hidden' : ''
                }`}
              >
                <RichTextRenderer  html={description} />
              </div>
              
              {!expanded && shouldTruncate && (
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent pointer-events-none" />
              )}
            </div>
          ) : (
            <p className="text-foreground/75 leading-relaxed text-[0.95rem] whitespace-pre-line">
              {expanded || !shouldTruncate ? description : truncatedPlainText}
            </p>
          )}

          {shouldTruncate && (
            <Button
              variant="ghost"
              className="mt-3 px-0 h-auto font-medium text-primary hover:text-primary/80 hover:bg-transparent"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? t("producer.about.readLess") : t("producer.about.readMore")}
            </Button>
          )}
        </div>
      </section>
    );
  }
);
